'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { SeverityChangeRequest } from '@terra/shared-contracts-findings';
import type { FindingSeverityType } from '@terra/shared-schemas-orchestrator';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  BadgeSeverity,
  BodyDefault,
  BodyStrong,
  Button,
  Field,
  MetaDefault,
  MonoDefault,
  Textarea,
  toasterApi,
  type BadgeSeverityType,
} from '@terra/ui-design-system';
import { useDecideSeverityChangeRequest } from '../../hooks/api/useDecideSeverityChangeRequest';
import { SEVERITY_CHANGE_REQUEST_CARD_ELEMENT_ID } from './SeverityChangeRequestHeaderPill';

type SeverityChangeRequestReviewCardProps = {
  findingId: string;
  currentSeverity: FindingSeverityType;
  severityChangeRequest: SeverityChangeRequest;
  canDecide: boolean;
  dateFormatter: {
    fromString: (date: string) => string;
  };
};

const toBadgeSeverity = (severity: FindingSeverityType): BadgeSeverityType =>
  severity === 'Info' ? 'Low' : (severity as BadgeSeverityType);

const toDateString = (value: Date | string): string =>
  value instanceof Date ? value.toISOString() : String(value);

const statusBadgeProps = (
  status: SeverityChangeRequest['status'],
): {
  borderColor: string;
  color: string;
  bg: string;
  labelKey: 'statusPending' | 'statusApproved' | 'statusRejected';
} => {
  switch (status) {
    case 'Pending':
      return {
        borderColor: 'border.severity.high',
        color: 'fg.severity.high',
        bg: 'transparent',
        labelKey: 'statusPending',
      };
    case 'Approved':
      return {
        borderColor: 'border.severity.low',
        color: 'fg.severity.low',
        bg: 'bg.severity.low',
        labelKey: 'statusApproved',
      };
    case 'Rejected':
      return {
        borderColor: 'border.neutral.subtle',
        color: 'fg.neutral.muted',
        bg: 'bg.neutral.subtle',
        labelKey: 'statusRejected',
      };
  }
};

export function SeverityChangeRequestReviewCard({
  findingId,
  currentSeverity,
  severityChangeRequest,
  canDecide,
  dateFormatter,
}: SeverityChangeRequestReviewCardProps) {
  const t = useTranslations(
    'designSystem.findings.findingDrawer.tabs.overview.severityChangeRequest',
  );
  const tCommon = useTranslations('common');
  const decideMutation = useDecideSeverityChangeRequest();
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const trimmedReason = rejectionReason.trim();
  const canConfirmReject =
    trimmedReason.length > 0 && !decideMutation.isPending;
  const statusStyle = statusBadgeProps(severityChangeRequest.status);
  const requestedAt = dateFormatter.fromString(
    toDateString(severityChangeRequest.createdAt),
  );
  const isPending = severityChangeRequest.status === 'Pending';
  const cardBg = isPending ? 'bg.accent.hoverAlpha' : 'bg.neutral.subtle';
  const cardBorder = isPending ? 'bg.accent.subtle' : 'border.neutral.subtle';

  const handleApprove = async () => {
    try {
      await decideMutation.mutateAsync({
        findingId,
        requestId: severityChangeRequest.id,
        body: { decision: 'Approved' },
      });
      toasterApi.success({
        title: t('toastApproveSuccessTitle'),
        description: t('toastApproveSuccessDescription'),
      });
    } catch (error: unknown) {
      console.warn('Failed to approve severity change request', error);
      toasterApi.error({
        title: t('toastDecideErrorTitle'),
        description: t('toastDecideErrorDescription'),
      });
    }
  };

  const handleReject = async () => {
    if (!canConfirmReject) {
      return;
    }

    try {
      await decideMutation.mutateAsync({
        findingId,
        requestId: severityChangeRequest.id,
        body: { decision: 'Rejected', reason: trimmedReason },
      });
      setIsRejecting(false);
      setRejectionReason('');
      toasterApi.success({
        title: t('toastRejectSuccessTitle'),
        description: t('toastRejectSuccessDescription'),
      });
    } catch (error: unknown) {
      console.warn('Failed to reject severity change request', error);
      toasterApi.error({
        title: t('toastDecideErrorTitle'),
        description: t('toastDecideErrorDescription'),
      });
    }
  };

  const reviewedAt =
    severityChangeRequest.status !== 'Pending'
      ? dateFormatter.fromString(toDateString(severityChangeRequest.reviewedAt))
      : null;

  return (
    <VStack
      id={SEVERITY_CHANGE_REQUEST_CARD_ELEMENT_ID}
      gap="3.5"
      alignItems="stretch"
      p="4"
      borderRadius="lg"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={cardBorder}
      bg={cardBg}
      scrollMarginTop="4"
      w="full"
    >
      <HStack gap="2.5" alignItems="center" w="full" flexWrap="wrap">
        <MonoDefault color="fg.neutral.subtle">
          {t('reviewCardEyebrow')}
        </MonoDefault>
        <Badge
          size="sm"
          borderWidth="1px"
          borderStyle="solid"
          borderColor={statusStyle.borderColor}
          color={statusStyle.color}
          bg={statusStyle.bg}
        >
          {t(statusStyle.labelKey)}
        </Badge>
        <Box ml="auto">
          <MetaDefault color="fg.neutral.subtle">
            {t('requestedBy', {
              name: severityChangeRequest.requestedByName,
              date: requestedAt,
            })}
          </MetaDefault>
        </Box>
      </HStack>

      <HStack gap="2.5" alignItems="center">
        <BadgeSeverity
          severity={toBadgeSeverity(severityChangeRequest.fromSeverity)}
          size="sm"
        >
          {severityChangeRequest.fromSeverity}
        </BadgeSeverity>
        <MetaDefault color="fg.neutral.subtle">→</MetaDefault>
        <BadgeSeverity
          severity={toBadgeSeverity(severityChangeRequest.toSeverity)}
          size="sm"
        >
          {severityChangeRequest.toSeverity}
        </BadgeSeverity>
      </HStack>

      <VStack gap="1.5" alignItems="stretch">
        <BodyStrong color="fg.neutral.muted">
          {t('justificationLabel')}
        </BodyStrong>
        <BodyDefault color="fg.neutral.default" whiteSpace="pre-wrap">
          {severityChangeRequest.justification}
        </BodyDefault>
      </VStack>

      {severityChangeRequest.status === 'Approved' && reviewedAt ? (
        <VStack
          gap="1.5"
          alignItems="stretch"
          pt="3"
          borderTopWidth="1px"
          borderColor="border.neutral.subtle"
        >
          <BodyStrong color="fg.neutral.muted">
            {t('reviewedBy', {
              name: severityChangeRequest.reviewedByName,
              date: reviewedAt,
            })}
          </BodyStrong>
        </VStack>
      ) : null}

      {severityChangeRequest.status === 'Rejected' && reviewedAt ? (
        <VStack
          gap="1.5"
          alignItems="stretch"
          pt="3"
          borderTopWidth="1px"
          borderColor="border.neutral.subtle"
        >
          <BodyStrong color="fg.neutral.muted">
            {t('rejectionReasonTitle', {
              name: severityChangeRequest.reviewedByName,
              date: reviewedAt,
            })}
          </BodyStrong>
          <BodyDefault color="fg.neutral.default" whiteSpace="pre-wrap">
            {severityChangeRequest.rejectionReason}
          </BodyDefault>
        </VStack>
      ) : null}

      {isPending && canDecide && !isRejecting ? (
        <HStack gap="2" alignItems="center" pt="1" flexWrap="wrap">
          <Button
            size="sm"
            variant="solid"
            color="white"
            loading={decideMutation.isPending}
            disabled={decideMutation.isPending}
            onClick={() => {
              void handleApprove();
            }}
          >
            {t('approveChange')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={decideMutation.isPending}
            onClick={() => setIsRejecting(true)}
          >
            {tCommon('reject')}
          </Button>
          <Box ml="auto">
            <MetaDefault color="fg.neutral.subtle">
              {t('staysUntilReviewed', { severity: currentSeverity })}
            </MetaDefault>
          </Box>
        </HStack>
      ) : null}

      {isPending && canDecide && isRejecting ? (
        <VStack
          gap="2"
          alignItems="stretch"
          pt="3"
          borderTopWidth="1px"
          borderColor="border.neutral.subtle"
        >
          <Field.Root w="full" required>
            <Field.Label>
              <HStack gap="1.5" alignItems="baseline">
                <BodyStrong color="fg.neutral.muted">
                  {t('rejectionReasonLabel')}
                </BodyStrong>
                <MetaDefault color="fg.neutral.subtle">
                  {t('rejectionReasonHint')}
                </MetaDefault>
              </HStack>
            </Field.Label>
            <Textarea
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              placeholder={t('rejectionReasonPlaceholder', {
                severity: currentSeverity,
              })}
              rows={3}
              maxLength={2000}
              fontSize="sm"
            />
          </Field.Root>
          <HStack gap="2" alignItems="center">
            <Button
              size="sm"
              variant="solid"
              color="white"
              loading={decideMutation.isPending}
              disabled={!canConfirmReject}
              onClick={() => {
                void handleReject();
              }}
            >
              {t('rejectRequest')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={decideMutation.isPending}
              onClick={() => {
                setIsRejecting(false);
                setRejectionReason('');
              }}
            >
              {tCommon('cancel')}
            </Button>
          </HStack>
        </VStack>
      ) : null}
    </VStack>
  );
}
